/**
 * `/dev/primitives` — HoD showcase route for visual baseline generation.
 *
 * Phase 1 (post-walkthrough): covers each primitive × variant × size × theme.
 * This route is registered in the router unconditionally (no DEV guard) so
 * Playwright visual tests can hit it via `vite preview`. It renders purely
 * from props — no auth, no API calls.
 *
 * Spec: backend/docs/post_walkthrough_phase_1_kickoff.md §Acceptance criteria #10.
 * HoD accept URL: https://app.certainhr.ai/dev/primitives
 *
 * Sections:
 *   1. Button — 4 variants × 3 sizes
 *   2. StatusPill — tenant (5 values) × pipeline (5 values) × 2 sizes
 *   3. Toast — all 4 variants (rendered statically, no timer)
 *   4. Modal — trigger buttons to open each type
 *   5. PageHeader — all slot combinations
 */
import { useState } from "react";
import { Button } from "@/components/common/Button";
import { StatusPill } from "@/components/candidates/StatusPill";
import { useToastStore } from "@/stores/useToastStore";
import { useModalStore } from "@/stores/useModalStore";
import { Modal } from "@/components/common/Modal";
import { Toast } from "@/components/common/Toast";
import { PageHeader } from "@/components/layout/PageHeader";
import type { ToastVariant, ToastAction } from "@/stores/useToastStore";

// ── Section wrapper ──────────────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="mb-10">
      <h2 className="mb-4 border-b border-border-default pb-2 text-lg font-semibold text-text-emphasis">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <span className="w-40 shrink-0 text-xs font-medium text-text-tertiary">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

// ── Sections ─────────────────────────────────────────────────────────────

function ButtonSection(): JSX.Element {
  const variants = [
    "primary",
    "secondary",
    "tertiary",
    "destructive",
  ] as const;
  const sizes = ["sm", "md", "lg"] as const;

  return (
    <Section title="Button — 4 variants × 3 sizes">
      {variants.map((variant) => (
        <Row key={variant} label={variant}>
          {sizes.map((size) => (
            <Button key={size} variant={variant} size={size}>
              {variant} / {size}
            </Button>
          ))}
        </Row>
      ))}
      <Row label="ghost (deprecated alias)">
        <Button variant="ghost">ghost</Button>
      </Row>
      <Row label="danger (deprecated alias)">
        <Button variant="danger">danger</Button>
      </Row>
      <Row label="loading state">
        <Button loading>Loading…</Button>
        <Button variant="secondary" loading>
          Loading…
        </Button>
      </Row>
    </Section>
  );
}

function StatusPillSection(): JSX.Element {
  const tenantValues = [
    "active",
    "hired",
    "rejected",
    "opted_out",
    "phone_screen",
  ] as const;

  const pipelineValues = [
    "active",
    "phone_screen",
    "interview_scheduled",
    "interviewed",
    "offer",
  ] as const;

  return (
    <Section title="StatusPill — tenant + pipeline × sm/md">
      <Row label="tenant / md">
        {tenantValues.map((v) => (
          <StatusPill key={v} variant="tenant" value={v} size="md" />
        ))}
      </Row>
      <Row label="tenant / sm">
        {tenantValues.map((v) => (
          <StatusPill key={v} variant="tenant" value={v} size="sm" />
        ))}
      </Row>
      <Row label="pipeline / md">
        {pipelineValues.map((v) => (
          <StatusPill key={v} variant="pipeline" value={v} size="md" />
        ))}
      </Row>
      <Row label="pipeline / sm">
        {pipelineValues.map((v) => (
          <StatusPill key={v} variant="pipeline" value={v} size="sm" />
        ))}
      </Row>
    </Section>
  );
}

function ToastSection(): JSX.Element {
  const addToast = useToastStore((s) => s.addToast);

  const examples: Array<{
    variant: ToastVariant;
    msg: string;
    action?: ToastAction;
  }> = [
    { variant: "success", msg: "Resume parsed. Candidate added to pipeline." },
    {
      variant: "error",
      msg: "Couldn't parse resume — saved without auto-fill.",
      action: { label: "Retry", onClick: () => {} },
    },
    { variant: "info", msg: "Evaluation queued. Results in ~30 seconds." },
    {
      variant: "warning",
      msg: "Ranking data is older than 7 days. Consider re-ranking.",
    },
  ];

  return (
    <Section title="Toast — 4 variants (click to trigger)">
      <div className="flex flex-wrap gap-2">
        {examples.map(({ variant, msg, action }) => (
          <Button
            key={variant}
            variant="secondary"
            size="sm"
            onClick={() => addToast(msg, variant, action)}
          >
            {variant} toast
          </Button>
        ))}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            addToast("First toast", "info");
            addToast("Second toast", "success");
            addToast("Third toast", "warning");
            addToast("Fourth toast (overflows max-3)", "error");
          }}
        >
          overflow (4 → max 3)
        </Button>
      </div>
      <p className="mt-2 text-xs text-text-tertiary">
        Success/info dismiss in 5s. Warning dismisses in 8s. Error never
        auto-dismisses.
      </p>
    </Section>
  );
}

function ModalSection(): JSX.Element {
  const openModal = useModalStore((s) => s.open);

  return (
    <Section title="Modal — confirm + legal reaccept + confirmCopy gate">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={() => openModal({ type: "confirm", props: {} })}
        >
          confirm modal
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            openModal({ type: "terms-reaccept", props: {} })
          }
        >
          terms-reaccept
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            openModal({
              type: "confirm",
              props: { confirmCopy: "delete-tenant" },
            })
          }
        >
          confirm + type-to-match
        </Button>
        <Button
          variant="destructive"
          onClick={() =>
            openModal({ type: "confirm", props: { confirmCopy: "danger-op" } })
          }
        >
          destructive + confirmCopy
        </Button>
      </div>
      <p className="mt-2 text-xs text-text-tertiary">
        Esc closes. Click outside closes. Default focus on Cancel.
      </p>
      <Modal />
    </Section>
  );
}

function PageHeaderSection(): JSX.Element {
  const [showSelector, setShowSelector] = useState(false);
  const [showStats, setShowStats] = useState(false);

  return (
    <Section title="PageHeader — slot combinations">
      <div className="mb-3 flex gap-2 text-xs">
        <label className="flex items-center gap-1 text-text-secondary">
          <input
            type="checkbox"
            checked={showSelector}
            onChange={(e) => setShowSelector(e.target.checked)}
          />
          contextSelector
        </label>
        <label className="flex items-center gap-1 text-text-secondary">
          <input
            type="checkbox"
            checked={showStats}
            onChange={(e) => setShowStats(e.target.checked)}
          />
          contextStats
        </label>
      </div>

      <div className="overflow-hidden rounded-lg border border-border-default">
        <PageHeader
          title="Dashboard"
          contextSelector={
            showSelector ? (
              <span className="rounded border border-border-strong bg-surface-card px-2 py-1 text-xs text-text-secondary">
                Job Selector
              </span>
            ) : undefined
          }
          contextStats={
            showStats ? (
              <span className="text-xs text-text-secondary">
                42 candidates · 3 ranked
              </span>
            ) : undefined
          }
          secondaryActions={[
            <Button key="rerank" variant="secondary" size="sm">
              Re-rank
            </Button>,
          ]}
          primaryAction={
            <Button variant="primary" size="sm">
              + Add Candidate
            </Button>
          }
        />
      </div>

      <div className="mt-3 overflow-hidden rounded-lg border border-border-default">
        <PageHeader title="Settings" meta="last updated 2 min ago" />
      </div>
    </Section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function DevPrimitives(): JSX.Element {
  return (
    <div
      data-testid="page-ready"
      className="min-h-screen bg-surface-canvas px-4 py-6 text-text-primary"
    >
      <h1 className="mb-2 text-2xl font-bold text-text-emphasis">
        /dev/primitives
      </h1>
      <p className="mb-8 text-sm text-text-secondary">
        Phase 1 — post-walkthrough primitive showcase. All variants × sizes ×
        themes. HoD acceptance surface.
      </p>

      <ButtonSection />
      <StatusPillSection />
      <ToastSection />
      <ModalSection />
      <PageHeaderSection />

      {/* Global Toast stack — renders outside the section */}
      <Toast />
    </div>
  );
}
